import { useMutation, useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { PartialExcept, ReservationBase } from '../../shared/types'
import { post } from '../fetch'

export type DashboardEntry = ReservationBase & { dateFrom: DateTime; dateTo: DateTime }

export type Filter = {
	locationFrom?: Array<string | number>
	locationTo?: Array<string | number>
	ignoreRentner?: boolean
}

const staleTime = 3_000 // 3 seconds only

export function useDashboardList(dateFrom: string, dateTo: string, filter: Filter) {
	return useQuery({
		queryKey: ['api/dashboard', { dateFrom, dateTo, ...filter }],
		queryFn: () => {
			return post<ReservationBase[]>(`/api/0/dashboard`, { dateFrom, dateTo, ...filter })
		},
		select: (data): Partial<Record<string, DashboardEntry[]>> => {
			const withDates = data
				.toSorted((a, b) => (a.date_from > b.date_from ? 1 : -1))
				.map(entry => ({
					...entry,
					dateFrom: DateTime.fromISO(entry.date_from),
					dateTo: DateTime.fromISO(entry.date_to),
				}))
			return Object.groupBy(withDates, entry => entry.vehicle_id)
		},
		staleTime,
	})
}

export function useDashboardUpdate() {
	return useMutation({
		mutationFn: ({ id, ...data }: PartialExcept<DashboardEntry, 'id'>) => {
			/**
			 * When changing vehicle, duration is unchanged, when changing duration, make sure to include pricing changes
			 */
			if (data.vehicle_id) {
				return post(`/api/0/reservation/${id}`, data)
			}
			return post(`/api/0/reservation/${id}/actions/change-dates`, data)
		},
		meta: {
			invalidateQueries: [['api/reservation'], ['api/dashboard'], [['api', 'expense']]],
		},
	})
}
